import type { IChoiceGroupOption, IStackStyles, IStackTokens } from "@fluentui/react";
import { Icon, Stack, TextField, TooltipHost, getTheme } from "@fluentui/react";
import colors from "../../../ui/colors.scss";
import React, { useState } from "react";
import css from "./action-config-node.scss";
import type { NodeKindDiscriminants as NodeKind } from "../../../api";
import { Antenna, DigitalInput, Zone } from "../../../api";
import { useTranslation } from "react-i18next";
import { NodeHandles } from "./restricted-handle";
import { useNodeId, useReactFlow } from "@xyflow/react";
import classNames from "classnames";
import type { ValidationProblem } from "../../../utils/validation";

const theme = getTheme();

export const digitalInputOptions = (t: (key: string) => string): IChoiceGroupOption[] => {
    return Object.values(DigitalInput).map((key, index) => {
        return { key, text: t(`component.actionConfigNodes.digitalInput.${index}`) };
    });
};

export const zoneOptions = (t: (key: string) => string): IChoiceGroupOption[] => {
    return Object.values(Zone).map((key, index) => {
        return { key, text: t(`component.actionConfigNodes.zone.${index}`) };
    });
};

export const indicationsZoneOptions = (t: (key: string) => string): IChoiceGroupOption[] => {
    return Object.values(Zone).map((key, index) => {
        return { key, text: t(`component.actionConfigNodes.indications.zone.${index}`) };
    });
};

export const antennaOptions = (t: (key: string) => string): IChoiceGroupOption[] => {
    return Object.values(Antenna).map((key, index) => {
        return { key, text: t(`component.actionConfigNodes.antenna.${index}`) };
    });
};

const stackStyle: IStackStyles = {
    root: {
        background: colors.primarywhite,
        borderRadius: theme.effects.roundedCorner4,
        boxShadow: theme.effects.elevation4,
    },
};

interface ActionConfigNodeProps {
    nodeKind: string;
    label?: string;
    invalid?: boolean;
    validationProblem?: ValidationProblem;
    children?: React.ReactNode;
    sidebar?: boolean;
    disabled?: boolean;
}

export type InterfaceToRecord<T> = {
    [K in keyof T]: T[K];
};

export interface ExtraNodeData {
    invalid?: boolean;
    validationProblem?: ValidationProblem;
}

const childrenToken: IStackTokens = { childrenGap: "1.5em" };

export default function ActionConfigNode(props: ActionConfigNodeProps): JSX.Element {
    const id = useNodeId();
    const { setNodes, setEdges, getNode } = useReactFlow();
    const [showLabel, setShowLabel] = useState(props.label !== undefined);

    const deleteNode = (): void => {
        setNodes((nodes) => nodes.filter((node) => node.id !== id));
        // Also delete edges that are connected to this node
        setEdges((edges) => edges.filter((edge) => edge.source !== id && edge.target !== id));
    };

    const { t } = useTranslation();
    const setLabel = (label?: string): void => {
        if (!id) {
            return;
        }
        const node = getNode(id);
        if (!node) {
            return;
        }
        node.data = {
            ...node.data,
            label: label,
        };
        setNodes((nodes) => nodes.filter((node) => node.id !== id).concat(node));
    };

    const deleteTooltipTargetElementRef: React.RefObject<HTMLButtonElement> =
        React.useRef<HTMLButtonElement>(null);
    const editLabelTooltipTargetElementRef: React.RefObject<HTMLButtonElement> =
        React.useRef<HTMLButtonElement>(null);

    let tooltipContent = "";
    if (props.validationProblem) {
        let code: string;
        let params: {} | { count: number; found: number };
        switch (props.validationProblem.code) {
            case "too_few_inputs":
                code = props.validationProblem.code;
                params = {
                    count: props.validationProblem.params["min_expected"] as number,
                    found: props.validationProblem.params["found"] as number,
                };
                break;
            case "incorrect_number_of_inputs":
                code = props.validationProblem.code;
                params = {
                    count: props.validationProblem.params["expected"] as number,
                    found: props.validationProblem.params["found"] as number,
                };
                break;
            default:
                code = "unknown";
                params = {};
        }
        tooltipContent = t(`validation.invalid.actionConfig.${code}`, params);
    }

    return (
        <Stack
            className={classNames({
                [css.actionConfigNode__disabled]: props.disabled,
                [css.actionConfigNode__invalid]: props.invalid,
                "react-flow__node": true,
                "react-flow__node-input": true,
            })}
            styles={stackStyle}
            style={{
                height: "100%",
                position: "relative",
                borderStyle: "unset",
                padding: "unset",
                width: props.sidebar ? "" : "unset",
            }}
        >
            <TooltipHost
                styles={{ root: { height: "100%" } }}
                content={tooltipContent}
                hidden={!props.invalid}
            >
                <Stack style={{ height: "100%" }}>
                    <div className={css.actionConfigNode__heading}>
                        <>{t(`component.actionConfigNodes.${props.nodeKind}.label`)}</>
                        {!props.sidebar && (
                            <TooltipHost
                                content={
                                    showLabel
                                        ? t("component.actionConfigNode.hideLabel")
                                        : t("component.actionConfigNode.editLabel")
                                }
                                calloutProps={{ target: editLabelTooltipTargetElementRef.current }}
                            >
                                <button
                                    ref={editLabelTooltipTargetElementRef}
                                    className={css.actionConfigNode__heading__button__label}
                                    onClick={() => setShowLabel(!showLabel)}
                                >
                                    <Icon iconName={showLabel ? "Hide" : "Label"} />
                                </button>
                            </TooltipHost>
                        )}
                        {!props.sidebar && (
                            <TooltipHost
                                content={t("component.actionConfigNode.delete")}
                                calloutProps={{ target: deleteTooltipTargetElementRef.current }}
                            >
                                <button
                                    ref={deleteTooltipTargetElementRef}
                                    className={css.actionConfigNode__heading__button}
                                    onClick={deleteNode}
                                >
                                    ×
                                </button>
                            </TooltipHost>
                        )}
                    </div>
                    {!props.sidebar && showLabel && (
                        <div className={css.actionConfigNode__label}>
                            <TextField
                                value={props.label}
                                onChange={(_e, value) => {
                                    setLabel(value);
                                }}
                                placeholder={t("component.actionConfigNode.description")}
                            />
                        </div>
                    )}
                    {props.children && (
                        <Stack className={css.actionConfigNode__body} tokens={childrenToken}>
                            {props.children}
                        </Stack>
                    )}
                </Stack>
            </TooltipHost>
            {!props.disabled && <NodeHandles nodeKind={props.nodeKind as NodeKind} />}
        </Stack>
    );
}
